import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { notificationAPI } from "../services/AdminPortal/notificationsAPI";
import { toast } from "react-toastify";
const staticAnnouncements = [
  {
    id: 1,
    message:
      "Reminder: All applications must be reviewed by the end of the day.",
    type: "Announcement",
    employeeType: null,
    date: "15/10/2023 10:00 AM",
  },
  {
    id: 2,
    message:
      "Training session on loan application processing scheduled for tomorrow at 2 PM.",
    type: "Credit Manager",
    employeeType: "Credit Manager",
    date: "15/10/2023 10:00 AM",
  },
  {
    id: 3,
    message:
      "Update: Interest rates have changed, please adjust the calculations accordingly.",
    type: "Staff",
    employeeType: "Staff",
    date: "15/10/2023 10:00 AM",
  },
  {
    id: 4,
    message:
      "Heads up: A new policy will be implemented next month regarding application approvals.",
    type: "Sub-Admin",
    employeeType: "Sub-Admin",
    date: "15/10/2023 10:00 AM",
  },
  {
    id: 5,
    message:
      "Congratulations! The team exceeded the monthly loan targets by 15%.",
    type: "Announcement",
    employeeType: null,
    date: "15/10/2023 10:00 AM",
  },
];

const employeeTypes = [
  { value: "", label: "Select Employee Type" },
  { value: "credit-manager", label: "Credit Manager" },
  { value: "staff", label: "Staff" },
  { value: "subAdmin", label: "Sub-Admin" },
];

const typeColors = {
  Announcement: "bg-[#FFCA2033]  text-[#FFCA20]",
  "Credit Manager": "bg-[#00800033] text-[#00D200]",
  Staff: "bg-[#FF660033] text-[#FF6600]",
  "Sub-Admin": "bg-[#80008033] text-[#FF4FFF]",
};

const getPlaceholderCategories = (role) => {
  if (role === "staff") {
    return [
      { key: "all", label: "All" },
      { key: "announcement", label: "Announcement" },
      { key: "staff", label: "Staff" },
    ];
  } else if (role === "credit-manager" || role === "credit_manager") {
    return [
      { key: "all", label: "All" },
      { key: "announcement", label: "Announcement" },
      { key: "credit_manager", label: "Credit Manager" },
    ];
  }
  return [
    { key: "all", label: "All" },
    { key: "announcement", label: "Announcement" },
    { key: "credit_manager", label: "Credit Manager" },
    { key: "staff", label: "Staff" },
    { key: "sub_admin", label: "Sub-Admin" },
  ];
};

// Format timestamp function
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = (now - date) / (1000 * 60 * 60);

  if (diffInHours < 24) {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } else if (diffInHours < 48) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString();
  }
};

const Announcement = () => {
  const { user } = useAuth();
  const placeholderCategories = getPlaceholderCategories(user?.role);
  const [activeTab, setActiveTab] = useState("all");
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAnnounceModal, setShowAnnounceModal] = useState(false);
  const [shareType, setShareType] = useState("");
  const [shareMsg, setShareMsg] = useState("");
  const [announceMsg, setAnnounceMsg] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch announcements on mount
  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await notificationAPI.getAllAnnouncements();
      setAnnouncements(res.data || []);
    } catch (err) {
      setError("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  // Filter announcements by tab
  let filteredAnnouncements;
  if (activeTab === "all") {
    if (user?.role === "staff") {
      filteredAnnouncements = announcements.filter(
        (a) =>
          a.notificationType === "Announcement" ||
          a.notificationType === "Staff"
      );
    } else if (
      user?.role === "credit-manager" ||
      user?.role === "credit_manager"
    ) {
      filteredAnnouncements = announcements.filter(
        (a) =>
          a.notificationType === "Announcement" ||
          a.notificationType === "Credit Manager"
      );
    } else {
      filteredAnnouncements = announcements;
    }
  } else {
    filteredAnnouncements = announcements.filter(
      (a) => a.notificationType.toLowerCase().replace(/ /g, "_") === activeTab
    );
  }

  // Deduplicate by message + sentAt
  const uniqueAnnouncements = [];
  const seen = new Set();
  for (const a of filteredAnnouncements) {
    const key = a.message + (a.sentAt || "");
    if (!seen.has(key)) {
      uniqueAnnouncements.push(a);
      seen.add(key);
    }
  }

  // Send announcement to all
  const handleSendAnnouncement = async () => {
    if (!announceMsg.trim()) return;
    setLoading(true);
    setError("");
    try {
      await notificationAPI.sendAnnouncementToAll({
        message: announceMsg,
        senderUserId: user.id,
      });
      toast.success("Message sent successfully");
      setShowAnnounceModal(false);
      setAnnounceMsg("");
      fetchAnnouncements();
    } catch (err) {
      toast.error("Failed to send announcement");
      setError("Failed to send announcement");
    } finally {
      setLoading(false);
    }
  };

  // Send to selected employee type
  const handleSendToEmployeeType = async () => {
    if (!shareMsg.trim() || !shareType) return;
    setLoading(true);
    setError("");
    try {
      await notificationAPI.sendAnnouncementToTypes({
        message: shareMsg,
        senderUserId: user.id,
        employeeTypes: [shareType],
      });
      toast.success("Message sent successfully");
      setShowShareModal(false);
      setShareMsg("");
      setShareType("");
      fetchAnnouncements();
    } catch (err) {
      toast.error("Failed to send message");
      setError("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-inter p-2 md:p-4">
      {/* Top Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-2 md:px-8 mb-2 gap-3 overflow-hidden">
        <h2 className="text-xl font-bold">Recent Announcements</h2>
        {(user?.role === "admin" || user?.role === "subAdmin") && (
          <div className="flex flex-wrap gap-3 w-full sm:w-auto">
            <button
              className="border-2 border-solid border-[#FFD600] text-[#FFD600] font-semibold rounded px-3 md:px-4 py-1 md:py-2 flex items-center gap-1 md:gap-2 text-sm md:text-base transition"
              onClick={() => setShowShareModal(true)}
            >
              <span className="hidden sm:inline">Share to</span> Employee Type{" "}
              <svg
                width="25"
                height="21"
                viewBox="0 0 25 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.25 10.5C16.25 11.9062 15.1172 13 13.75 13H8.75L4.49219 15.4219C4.375 15.5 4.29688 15.5 4.21875 15.5C3.94531 15.5 3.75 15.3438 3.75 15.0312V13H2.5C1.09375 13 0 11.9062 0 10.5V3C0 1.63281 1.09375 0.5 2.5 0.5H13.75C15.1172 0.5 16.25 1.63281 16.25 3V10.5ZM8.24219 11.125H13.75C14.0625 11.125 14.375 10.8516 14.375 10.5V3C14.375 2.6875 14.0625 2.375 13.75 2.375H2.5C2.14844 2.375 1.875 2.6875 1.875 3V10.5C1.875 10.8516 2.14844 11.125 2.5 11.125H5.625V12.6484L7.8125 11.3984L8.24219 11.125ZM22.5 5.5C23.8672 5.5 25 6.63281 25 8V15.5C25 16.9062 23.8672 18 22.5 18H21.25V20.1484C21.25 20.4609 20.8594 20.6172 20.625 20.4609L17.3828 18H12.5C11.0938 18 10 16.9062 10 15.5V14.25H13.75C15.7812 14.25 17.5 12.5703 17.5 10.5V5.5H22.5Z"
                  fill="#FFCA20"
                />
              </svg>
            </button>
            <button
              className="bg-[#FFD600] text-black font-semibold rounded px-3 md:px-4 py-1 md:py-2 flex items-center gap-1 md:gap-2 text-sm md:text-base hover:bg-[#ffe066] transition"
              onClick={() => setShowAnnounceModal(true)}
            >
              Announcement
              <svg
                width="23"
                height="19"
                viewBox="0 0 23 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21.5625 0.75C22.0703 0.75 22.5 1.17969 22.5 1.6875V17.3125C22.5 17.8594 22.0703 18.25 21.5625 18.25C21.0156 18.25 20.625 17.8594 20.625 17.3125V17.1953L13.3594 15.1641C13.125 16.9219 11.6406 18.25 9.84375 18.25C7.85156 18.25 6.25 16.6484 6.25 14.6562C6.25 14.1875 6.32812 13.6797 6.52344 13.25L1.79688 11.9219C1.71875 12.3125 1.36719 12.625 0.9375 12.625C0.390625 12.625 0 12.2344 0 11.6875V7.3125C0 6.80469 0.390625 6.375 0.9375 6.375C1.36719 6.375 1.71875 6.72656 1.79688 7.11719L20.625 1.84375V1.6875C20.625 1.17969 21.0156 0.75 21.5625 0.75ZM11.5625 14.6562H11.5234L8.39844 13.7578C8.20312 14.0312 8.125 14.3438 8.125 14.6562C8.125 15.6328 8.86719 16.375 9.84375 16.375C10.7812 16.375 11.5625 15.6328 11.5625 14.6562ZM1.875 9.96875L20.625 15.2422V3.79688L1.875 9.07031V9.96875Z"
                  fill="#131313"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-[#181818] rounded-t-lg px-2 md:px-4 pt-4 pb-2 flex flex-wrap gap-2">
        {placeholderCategories.map((cat) => (
          <button
            key={cat.key}
            className={`px-3 md:px-5 py-1 md:py-2 rounded-full text-sm md:text-base font-medium border border-[#333] transition-all ${
              activeTab === cat.key
                ? "  border-solid border-[#FFD600] text-[#FFD600] bg-[#FFCA2033]"
                : "bg-transparent text-white hover:bg-[#222]"
            }`}
            onClick={() => setActiveTab(cat.key)}
          >
            {cat.label}
          </button>
        ))}
      </div>
      {/* Announcements List */}
      <div className="bg-[#181818] rounded-b-lg px-2 md:px-4 pb-4 min-h-[300px] divide-y divide-[#333]">
        {uniqueAnnouncements.map((a) => (
          <div
            key={a._id}
            className="py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2"
          >
            <div className="w-full sm:w-auto">
              <div className="text-sm md:text-[16px] mb-1">{a.message}</div>
              <div className="text-xs text-gray-400">
                {formatTimestamp(a.date || a.sentAt)}
              </div>
            </div>
            <div
              className={`sm:ml-4 px-2 md:px-3 py-1 rounded font-semibold text-xs whitespace-nowrap ${
                typeColors[a.notificationType] || "bg-gray-700 text-gray-300"
              }`}
            >
              {a.notificationType}
            </div>
          </div>
        ))}
      </div>

      {/* Share to Employee Type Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-[#181818] rounded-lg p-4 md:p-6 w-full max-w-md border border-[#333]">
            <div className="flex items-center gap-2 mb-2 text-lg font-semibold">
              <span className="text-[#FFD600] text-xl">&#128652;</span>
              Message Employee Type
            </div>
            <hr className="border-[#333] mb-4" />
            <div className="mb-3">
              <label className="block mb-1 font-medium">Share to</label>
              <select
                className="w-full bg-[#111] border border-[#333] rounded px-3 py-2 text-white focus:outline-none"
                value={shareType}
                onChange={(e) => setShareType(e.target.value)}
              >
                {employeeTypes.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-medium">Compose Message</label>
              <textarea
                className="w-full h-24 bg-[#111] border border-[#333] rounded px-3 py-2 text-white focus:outline-none resize-none"
                placeholder="Enter your message here"
                value={shareMsg}
                onChange={(e) => setShareMsg(e.target.value)}
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
              <button
                className="border border-[#333] text-white rounded px-3 md:px-4 py-1 md:py-2 hover:bg-[#222] text-sm md:text-base"
                onClick={() => setShowShareModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-green-500 text-black font-semibold rounded px-3 md:px-4 py-1 md:py-2 flex items-center justify-center gap-1 md:gap-2 hover:bg-green-600 transition text-sm md:text-base"
                onClick={handleSendToEmployeeType}
              >
                Send Message{" "}
                <span className="text-base md:text-lg">&#9993;</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnounceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-[#181818] rounded-lg p-4 md:p-6 w-full max-w-md border border-[#333]">
            <div className="flex items-center gap-2 mb-1 text-lg font-semibold">
              <span className="text-[#FFD600] text-xl">&#9993;</span>
              Announcement
            </div>
            <div className="text-xs text-gray-400 mb-2 ml-7">
              This message will be sent to all employees.
            </div>
            <hr className="border-[#333] mb-4" />
            <div className="mb-4">
              <label className="block mb-1 font-medium">Compose Message</label>
              <textarea
                className="w-full h-24 bg-[#111] border border-[#333] rounded px-3 py-2 text-white focus:outline-none resize-none"
                placeholder="Enter your message here"
                value={announceMsg}
                onChange={(e) => setAnnounceMsg(e.target.value)}
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
              <button
                className="border border-[#333] text-white rounded px-3 md:px-4 py-1 md:py-2 hover:bg-[#222] text-sm md:text-base"
                onClick={() => setShowAnnounceModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-green-500 text-black font-semibold rounded px-3 md:px-4 py-1 md:py-2 flex items-center justify-center gap-1 md:gap-2 hover:bg-green-600 transition text-sm md:text-base"
                onClick={handleSendAnnouncement}
              >
                Send Message{" "}
                <span className="text-base md:text-lg">&#9993;</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcement;
